import { useState, useEffect, useCallback, useRef } from 'react';
import { listFilterPosts, listPosts, listPseoPosts, searchPostsAdvanced } from '../../services/PostService';
import { listFilterSeekingPosts, listSeekingPosts, searchSeekingPostsAdvanced } from '../../services/SeekingPostService';
import { isRemoteNation } from './useReferenceData';

export const PAGE_SIZE = 10;

export const ORDER_OPTIONS = {
  most_recent: { sort: 'insertData', direction: 'desc' },
  oldest_first: { sort: 'insertData', direction: 'asc' },
  highest_salary: { sort: 'salary', direction: 'desc' },
  lowest_salary: { sort: 'salary', direction: 'asc' },
  highest_roles_available: { sort: 'numberRoles', direction: 'desc' },
  lowest_roles_available: { sort: 'numberRoles', direction: 'asc' },
};

// Remembered once we know the backend has no /search/advanced endpoint yet.
let advancedSearchUnavailable = false;

function buildFilters({ text, nation, country, withDescription }) {
  const remote = isRemoteNation(nation);
  return {
    keyword: text?.trim() || undefined,
    withDescription: Boolean(withDescription),
    remote: remote || undefined,
    placeId: !remote && country?.countryId ? country.countryId : undefined,
    nationTag: !remote && nation?.nationTag ? nation.nationTag : undefined,
    nationId: !remote && nation?.nationId ? String(nation.nationId) : undefined,
    nationName: !remote && nation?.nationName ? nation.nationName : undefined,
  };
}

// How many recent posts the fallback downloads when it has to filter in the browser.
const LOCAL_FILTER_WINDOW = 500;

const matchesLocation = (post, filters) => {
  if (!post) return false;
  if (filters.remote) return post.remote === true;
  if (filters.placeId) return String(post.place) === String(filters.placeId);
  if (filters.nationTag) {
    const tag = filters.nationTag.toLowerCase();
    return (post.nationTag || '').toLowerCase() === tag
      || String(post.nation) === String(filters.nationId)
      || (post.nation || '').toLowerCase() === (filters.nationName || '').toLowerCase();
  }
  return true;
};

const matchesKeyword = (post, keyword) => {
  if (!keyword) return true;
  const k = keyword.toLowerCase();
  return [post.title, post.company, post.task].some((v) => String(v || '').toLowerCase().includes(k));
};

/** Builds a Spring-like page from a list that was filtered in the browser, so totals stay correct. */
function paginateLocally(list, page) {
  const totalElements = list.length;
  return {
    content: list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    totalElements,
    totalPages: Math.max(1, Math.ceil(totalElements / PAGE_SIZE)),
  };
}

/**
 * Only used while the backend has no /search/advanced endpoint yet.
 * Filtering a single server page in the browser gave wrong totals (e.g. "298.143 results"
 * and thousands of pages with one job each), so every branch here returns consistent pages.
 */
async function legacySearch({ seek, filters, page, order }) {
  const hasLocation = filters.remote || filters.placeId || filters.nationTag;

  if (!filters.keyword && !hasLocation) {
    const res = await (seek ? listSeekingPosts : listPosts)(page - 1, PAGE_SIZE, 'insertData');
    return res.data;
  }

  // Keyword only: the keyword endpoint already paginates on the server.
  if (!hasLocation) {
    const res = await (seek ? listFilterSeekingPosts : listFilterPosts)(filters.keyword, page - 1, PAGE_SIZE, filters.withDescription, order.sort, order.direction);
    return res.data;
  }

  // Job offers by nation / remote: the SEO endpoint filters on the server with correct totals.
  if (!seek && !filters.placeId) {
    const res = await listPseoPosts(filters.keyword || '', filters.remote ? '' : filters.nationTag, Boolean(filters.remote), page - 1, PAGE_SIZE);
    return res.data;
  }

  // City filter (or seeking posts): download the most recent posts once and paginate locally.
  let source;
  if (!seek) {
    const res = await listPseoPosts(filters.keyword || '', filters.nationTag, false, 0, LOCAL_FILTER_WINDOW);
    source = res.data?.content || [];
  } else {
    const res = await listSeekingPosts(0, LOCAL_FILTER_WINDOW, 'insertData');
    source = (res.data?.content || []).filter((p) => matchesKeyword(p, filters.keyword));
  }
  return paginateLocally(source.filter((p) => matchesLocation(p, filters)), page);
}

/**
 * Loads a page of job offers (or "seeking" posts) with every filter applied server-side.
 * Stale responses are ignored, so typing / clicking fast never shows results of an older query.
 */
export default function usePosts({ seek, page, text, nation, country, withDescription, orderKey, refreshKey }) {
  const [posts, setPosts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const requestId = useRef(0);

  // Keyed on the ISO tag so swapping the URL placeholder for the loaded nation does not refetch.
  const nationKey = nation ? String(nation.nationTag || nation.nationId || '').toLowerCase() : '';
  const countryKey = country ? String(country.countryId) : '';

  const fetchPosts = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(false);
    const order = ORDER_OPTIONS[orderKey] || ORDER_OPTIONS.most_recent;
    const filters = buildFilters({ text, nation, country, withDescription });
    try {
      let data;
      if (!advancedSearchUnavailable) {
        try {
          const fn = seek ? searchSeekingPostsAdvanced : searchPostsAdvanced;
          const res = await fn(filters, page - 1, PAGE_SIZE, order.sort, order.direction);
          data = res.data;
        } catch (e) {
          const status = e?.response?.status;
          // An older backend answers 404/405, or a 500 from its catch-all handler
          // ("/{postId}/submissions" matches the path, so it reports an unsupported method).
          const missingEndpoint = status === 404 || status === 405
            || (status === 500 && /NoResourceFound|NoHandlerFound|MethodNotSupported/i.test(String(e?.response?.data?.error || '')));
          if (missingEndpoint) {
            advancedSearchUnavailable = true;
          } else {
            throw e;
          }
        }
      }
      if (!data) {
        data = await legacySearch({ seek, filters, page, order });
      }
      if (id !== requestId.current) return;
      setPosts(Array.isArray(data?.content) ? data.content : []);
      setTotalPages(Math.max(1, data?.totalPages || 1));
      setTotalElements(typeof data?.totalElements === 'number' ? data.totalElements : (data?.content || []).length);
    } catch (e) {
      console.error(e);
      if (id !== requestId.current) return;
      setPosts([]);
      setTotalPages(1);
      setTotalElements(0);
      setError(true);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seek, page, text, nationKey, countryKey, withDescription, orderKey]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts, refreshKey]);

  /** Update one post in place (e.g. after toggling a favourite) without reloading the page. */
  const updatePost = useCallback((postId, updater) => {
    setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, ...updater(p) } : p)));
  }, []);

  return { posts, totalPages, totalElements, loading, error, reload: fetchPosts, updatePost };
}
